import {
  AddressBookTabs,
  Info,
  UserCheck,
  UserCircleDashed,
  UserMinus,
  Users,
  Warning,
} from "@phosphor-icons/react";

import IconAvatar from "common/components/IconAvatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import type { EmailOutreachType } from "common/types";

import type { CrmExportSummary } from "../../../../types";

interface ExportSummaryProps {
  type: EmailOutreachType;
  summary: CrmExportSummary;
}

export default function ExportSummary({ summary, type }: ExportSummaryProps) {
  const label = `${type.charAt(0).toUpperCase()}${type.slice(1)}`;
  const isInstantly = type === "instantly";

  const {
    total_sent: totalSent,
    leads_uploaded: leadsUploaded,
    skipped_count: skippedCount,
    invalid_email_count: invalidEmailCount,
    incomplete_count: incompleteCount,
    duplicate_email_count: duplicateEmailCount,
    duplicated_leads: duplicateLeads,
  } = summary;

  const summaryItems = [
    {
      icon: AddressBookTabs,
      label: "Total sent",
      value: totalSent,
      infoText: `The total number of leads sent from Aimfox to ${label}`,
    },
    {
      icon: UserCheck,
      label: "Successfully created",
      value: leadsUploaded,
      infoText: `The number of leads that were successfully created in ${label}`,
    },
    ...(isInstantly
      ? [
          {
            icon: UserMinus,
            label: "Skipped",
            value: skippedCount,
            infoText:
              "The number of leads skipped due to the leads already being in the campaign/list",
          },
        ]
      : []),
    {
      icon: Warning,
      label: "Invalid email",
      value: invalidEmailCount,
      infoText: `The number of leads skipped due to an invalid email format or a missing email address. ${label} campaigns require leads to have a valid email address${isInstantly ? ", while Lists do not" : ""}`,
    },
    {
      icon: UserCircleDashed,
      label: "Incomplete",
      value: incompleteCount,
      infoText: `The number of leads skipped due to missing emails${isInstantly ? " or names" : ""}. Leads sent to campaigns must have an email address.${isInstantly ? " Leads sent to lists must have either a name or email address" : ""}`,
    },
    {
      icon: Users,
      label: "Duplicate email",
      value: duplicateEmailCount,
      infoText:
        "The number of leads skipped because their email was found multiple times in the leads sent from Aimfox",
    },
    {
      icon: Users,
      label: "Duplicate lead",
      value: duplicateLeads,
      infoText: `The number of leads who were skipped due to their email addresses already existing  in ${label}`,
    },
  ];

  return (
    <section className="py-4 my-4 overflow-y-auto scrollbar-thin">
      {summaryItems.map(({ label, value, icon: Icon, infoText }) => (
        <div
          key={label}
          className="flex w-full items-center border-b border-b-black-200 py-3 last:border-b-0 last:pb-0 first:pt-0"
        >
          <IconAvatar size="sm">
            <Icon />
          </IconAvatar>
          <span className="ml-2 mr-1 text-button-md">{label}</span>
          <Tooltip>
            <TooltipTrigger>
              <Info weight="fill" size={20} className="fill-black-300" />
            </TooltipTrigger>
            <TooltipContent>{infoText}</TooltipContent>
          </Tooltip>
          <span className="ml-auto text-button-sm">{value}</span>
        </div>
      ))}
    </section>
  );
}
